import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Truck, Shield, Users, Leaf, ShieldCheck, Store, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { categoriesAPI, seedAPI, productsAPI, settingsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { normCat, RUNGIS_IMAGE } from '../components/home/constants';
import { useParisWeather } from '../components/home/WeatherWidget';
import { HeroSection } from '../components/home/HeroSection';
import { BentoGrid } from '../components/home/BentoGrid';
import { FamilyScrollMenu } from '../components/home/FamilyScrollMenu';
import { CategoryCarousel } from '../components/home/CategoryCarousel';
import { WeeklyOffers } from '../components/home/WeeklyOffers';
import { PromoBanner } from '../components/home/PromoBanner';
import { useLanguage } from '../context/LanguageContext';

const FEATURES_DATA = [
    { icon: Truck, titleKey: 'livraisonExpress', descKey: 'livraisonExpressDesc', color: 'bg-emerald-500' },
    { icon: ShieldCheck, titleKey: 'qualiteGarantie', descKey: 'qualiteGarantieDesc', color: 'bg-blue-500' },
    { icon: Store, titleKey: 'prixGrossiste', descKey: 'prixGrossisteDesc', color: 'bg-amber-500' },
    { icon: Phone, titleKey: 'service7j', descKey: 'service7jDesc', color: 'bg-purple-500' },
];

const SEASONAL_ORDER = ['cat-fruits', 'cat-legumes', 'cat-plantes', 'cat-bio'];
const BENTO_ORDER = ['cat-fruits', 'cat-legumes', 'cat-bio', 'cat-plantes'];

export const Home = () => {
    const { isAuthenticated } = useAuth();
    const { t, monthName } = useLanguage();
    const [categories, setCategories] = useState([]);
    const [offersByCategory, setOffersByCategory] = useState([]);
    const [seasonalProducts, setSeasonalProducts] = useState([]);
    const [homepageSettings, setHomepageSettings] = useState({});
    const [loading, setLoading] = useState(true);
    const [promoExpired, setPromoExpired] = useState(false);
    const { weather, forecast, parisTime } = useParisWeather();
    const [showForecast, setShowForecast] = useState(false);

    const currentMonth = new Date().getMonth() + 1;
    const currentMonthName = monthName(currentMonth - 1);

    useEffect(() => {
        const init = async () => {
            try {
                await seedAPI.seed().catch(() => {});
                settingsAPI.trackVisit().catch(() => {});
                const [catRes, offersRes, seasonalRes, settingsRes] = await Promise.all([
                    categoriesAPI.getAll(),
                    productsAPI.getWeeklyOffersByCategory(),
                    productsAPI.getSeasonal(currentMonth),
                    settingsAPI.getHomepage().catch(() => ({ data: {} }))
                ]);
                setCategories(catRes.data.categories || []);
                setOffersByCategory(offersRes.data.categories || []);
                setSeasonalProducts(seasonalRes.data.products || []);
                setHomepageSettings(settingsRes.data || {});
            } catch (error) {
                console.error('Error loading data:', error);
            } finally {
                setLoading(false);
            }
        };
        init();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once on mount, APIs are stable module refs
    }, [currentMonth]);

    const hiddenCategories = useMemo(() => {
        const hidden = [];
        if (!homepageSettings.show_african_homepage) hidden.push('cat-african');
        if (!homepageSettings.show_cosmetics_homepage) hidden.push('cat-cosmetics');
        return hidden;
    }, [homepageSettings.show_african_homepage, homepageSettings.show_cosmetics_homepage]);

    const promoEndDate = homepageSettings.promo_end_date;
    const isPromoActive = promoEndDate && !promoExpired && new Date(promoEndDate).getTime() > Date.now();
    const showWeeklyOffers = offersByCategory.length > 0;

    const visibleCategories = useMemo(() =>
        categories
            .map(c => ({ ...c, id: normCat(c.id) }))
            .filter(c => !hiddenCategories.includes(c.id))
            .filter((c, i, arr) => arr.findIndex(x => x.id === c.id) === i),
        [categories, hiddenCategories]
    );

    const productsByCategory = useMemo(() =>
        seasonalProducts.reduce((acc, product) => {
            const catId = normCat(product.category_id || 'other');
            if (hiddenCategories.includes(catId)) return acc;
            if (!acc[catId]) acc[catId] = [];
            acc[catId].push(product);
            return acc;
        }, {}),
        [seasonalProducts, hiddenCategories]
    );

    const bentoCats = useMemo(() =>
        BENTO_ORDER.map(id => visibleCategories.find(c => c.id === id)).filter(Boolean),
        [visibleCategories]
    );

    const seasonalCount = useMemo(() =>
        seasonalProducts.filter(p => SEASONAL_ORDER.includes(p.category_id)).length,
        [seasonalProducts]
    );

    const activeCatIds = useMemo(() =>
        SEASONAL_ORDER.filter(catId => productsByCategory[catId]?.length > 0),
        [productsByCategory]
    );

    return (
        <div className="min-h-screen bg-[#FAFAF5]" data-testid="home-page">

            {/* PROMO BANNER WITH COUNTDOWN */}
            {showWeeklyOffers && isPromoActive && (
                <PromoBanner
                    promoEndDate={promoEndDate}
                    offersCount={offersByCategory.reduce((sum, c) => sum + (c.products?.length || c.count || 0), 0)}
                />
            )}

            {/* HERO */}
            <HeroSection
                weather={weather}
                forecast={forecast}
                parisTime={parisTime}
                showForecast={showForecast}
                setShowForecast={setShowForecast}
            />

            {/* QUICK FEATURES */}
            <section className="py-6 bg-white border-b border-gray-100" data-testid="features-strip">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {FEATURES_DATA.map((f) => (
                            <div key={f.titleKey} className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                                <div className={`w-10 h-10 rounded-xl ${f.color} flex items-center justify-center flex-shrink-0`}>
                                    <f.icon className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <p className="font-semibold text-sm text-gray-900">{t[f.titleKey]}</p>
                                    <p className="text-xs text-gray-500">{t[f.descKey]}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAMILY SCROLL MENU */}
            <FamilyScrollMenu categories={visibleCategories} loading={loading} />

            {/* WEEKLY OFFERS / PROMOS */}
            {showWeeklyOffers && (
                <WeeklyOffers
                    offersByCategory={offersByCategory}
                    hiddenCategories={hiddenCategories}
                    promoEndDate={isPromoActive ? promoEndDate : null}
                    setPromoExpired={setPromoExpired}
                />
            )}

            {/* BENTO GRID */}
            <BentoGrid bentoCats={bentoCats} loading={loading} />

            {/* SEASONAL PRODUCTS */}
            {Object.keys(productsByCategory).length > 0 && (
                <section className="py-16 md:py-24 bg-white" data-testid="seasonal-section">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-14">
                            <p className="text-xs font-bold uppercase tracking-widest text-emerald-600 mb-3">
                                <Leaf className="w-3 h-3 inline mr-1" />
                                {t.produitsDeSaison}
                            </p>
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">
                                {t.enSaisonEn}{' '}
                                <span className="text-emerald-600">{currentMonthName}</span>
                            </h2>
                            <p className="text-gray-500 mt-3">{seasonalCount} {t.produitsDisponibles}</p>
                        </div>

                        {activeCatIds.map(catId => {
                            const cat = categories.find(c => c.id === catId);
                            return (
                                <CategoryCarousel
                                    key={catId}
                                    products={productsByCategory[catId]}
                                    categoryId={catId}
                                    categoryName={cat?.name}
                                />
                            );
                        })}
                    </div>
                </section>
            )}

            {/* WHY PRIXDEAL */}
            <section className="py-20 bg-gray-900 text-white" data-testid="why-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <p className="text-xs font-bold uppercase tracking-widest text-emerald-400 mb-4">{t.pourquoiNousChoisir}</p>
                            <h2 className="text-3xl md:text-4xl font-bold mb-6 tracking-tight">
                                {t.excellenceTitle}
                            </h2>
                            <p className="text-gray-400 text-lg mb-10 leading-relaxed">
                                {t.excellenceDesc}
                            </p>
                            <div className="space-y-6">
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                                        <Truck className="w-6 h-6 text-emerald-400" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-white mb-1">{t.livraisonRapide}</h4>
                                        <p className="text-gray-400 text-sm">{t.livraisonRapideDesc}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-2xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                                        <Shield className="w-6 h-6 text-blue-400" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-white mb-1">{t.qualiteControlee}</h4>
                                        <p className="text-gray-400 text-sm">{t.qualiteControleeDesc}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-2xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                                        <Users className="w-6 h-6 text-amber-400" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-white mb-1">{t.equipeDediee}</h4>
                                        <p className="text-gray-400 text-sm">{t.equipeDedieeDesc}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="relative hidden lg:block">
                            <img src={RUNGIS_IMAGE} alt="Marché de Rungis" className="rounded-3xl shadow-2xl w-full object-cover aspect-[4/3]" loading="lazy" />
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-20 bg-emerald-600" data-testid="cta-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <p className="text-xs font-bold uppercase tracking-widest text-emerald-200 mb-4">{t.rejoignezNous}</p>
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 tracking-tight">
                        {t.pretANousRejoindre}
                    </h2>
                    <p className="text-emerald-100 text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
                        {t.ctaDesc}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Button asChild size="lg" className="bg-white text-emerald-700 hover:bg-emerald-50 h-14 px-8 rounded-full text-base font-semibold shadow-lg" data-testid="cta-register">
                            <Link to="/inscription">
                                {t.creerMonCompte}
                                <ArrowRight className="ml-2 w-5 h-5" />
                            </Link>
                        </Button>
                        {isAuthenticated && (
                            <Button asChild variant="outline" size="lg" className="h-14 px-8 rounded-full border-2 border-white/30 text-white hover:bg-white/10 text-base font-semibold" data-testid="cta-catalogue">
                                <Link to="/catalogue">
                                    {t.accederAuCatalogue}
                                </Link>
                            </Button>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;
